import { BaseDashlet } from "../../lq_js_common/dashboard/base_dashlet";
import { DashboardGraph } from "../../graphs/dashboard_graph";
import { createBoundedAxisTooltip, formatCountAxis } from "../../helpers/chart_common";
import { isColorBlindMode } from "../../helpers/colorblind";
import { lerpGreenToRedViaOrange } from "../../helpers/scaling";
import { disposeTooltips, enableTooltips, formatTimeRange } from "../../helpers/tooltips";
import { redactText } from "../../helpers/redact";
import {
    qualityFromCapacity,
    qualityFromQoo,
    qualityFromRetransmit,
    qualityFromRtt,
    sentimentState,
} from "../../helpers/site_health";
import { applyExecutiveMessage, buildExecutiveData, createExecutiveState } from "../executive/executive_data";

const SITE_RANKINGS_BUCKETS = 15;
const NO_DATA_COLOR = "rgba(122, 134, 154, 0.45)";
const STATUS_ORDER = {
    attention: 0,
    watch: 1,
    healthy: 2,
    nodata: 3,
};

function toFiniteNumber(value) {
    if (value === null || value === undefined) return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

function medianIgnoringNull(values) {
    const numbers = [];
    (Array.isArray(values) ? values : []).forEach((value) => {
        const numeric = toFiniteNumber(value);
        if (numeric !== null) {
            numbers.push(numeric);
        }
    });
    if (numbers.length === 0) return null;
    numbers.sort((a, b) => a - b);
    const mid = Math.floor(numbers.length / 2);
    if (numbers.length % 2 === 1) {
        return numbers[mid];
    }
    return (numbers[mid - 1] + numbers[mid]) / 2;
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function escapeAttr(value) {
    return escapeHtml(value);
}

function periodBuckets(values, ticks) {
    const series = Array.isArray(values) ? values : [];
    const tickSeries = Array.isArray(ticks) ? ticks : [];
    const len = tickSeries.length > 0 ? tickSeries.length : series.length;
    if (len <= 0) {
        return {
            values: new Array(SITE_RANKINGS_BUCKETS).fill(null),
            ranges: new Array(SITE_RANKINGS_BUCKETS).fill(null),
        };
    }

    const alignedSeries = series.slice(0, len);
    const alignedTicks = tickSeries.slice(0, len);
    while (alignedSeries.length < len) alignedSeries.push(null);
    while (alignedTicks.length < len) alignedTicks.push(null);

    if (len < SITE_RANKINGS_BUCKETS) {
        const pad = SITE_RANKINGS_BUCKETS - len;
        return {
            values: new Array(pad).fill(null).concat(alignedSeries),
            ranges: new Array(pad).fill(null).concat(alignedTicks.map((tick) => {
                const numeric = toFiniteNumber(tick);
                return numeric === null ? null : { start: numeric, end: numeric };
            })),
        };
    }

    const bucketValues = [];
    const ranges = [];
    for (let i = 0; i < SITE_RANKINGS_BUCKETS; i += 1) {
        const start = Math.floor((i * len) / SITE_RANKINGS_BUCKETS);
        const nextStart = Math.floor(((i + 1) * len) / SITE_RANKINGS_BUCKETS);
        const end = Math.max(start, nextStart - 1);
        bucketValues.push(medianIgnoringNull(alignedSeries.slice(start, end + 1)));
        const startTick = toFiniteNumber(alignedTicks[start]);
        const endTick = toFiniteNumber(alignedTicks[end]);
        ranges.push(startTick === null || endTick === null ? null : { start: startTick, end: endTick });
    }
    return { values: bucketValues, ranges };
}

function periodPairSummary(topValues, bottomValues, ticks) {
    const top = periodBuckets(topValues, ticks);
    const bottom = periodBuckets(bottomValues, ticks);
    return {
        top: medianIgnoringNull(top.values),
        bottom: medianIgnoringNull(bottom.values),
        topBuckets: top.values,
        bottomBuckets: bottom.values,
        ranges: top.ranges.length > 0 ? top.ranges : bottom.ranges,
        rangeText: formatSeriesRange(ticks, top.ranges.length > 0 ? top.ranges : bottom.ranges),
    };
}

function periodSeriesSummary(values, ticks) {
    const bucketed = periodBuckets(values, ticks);
    return {
        value: medianIgnoringNull(bucketed.values),
        buckets: bucketed.values,
        ranges: bucketed.ranges,
        rangeText: formatSeriesRange(ticks, bucketed.ranges),
    };
}

function formatSeriesRange(ticks, fallbackRanges = []) {
    const finiteTicks = (Array.isArray(ticks) ? ticks : []).map(toFiniteNumber).filter((value) => value !== null);
    if (finiteTicks.length >= 2) {
        return formatTimeRange(finiteTicks[0], finiteTicks[finiteTicks.length - 1]);
    }
    const ranges = (Array.isArray(fallbackRanges) ? fallbackRanges : []).filter((range) => range?.start !== undefined && range?.end !== undefined);
    if (ranges.length > 0) {
        return formatTimeRange(ranges[0].start, ranges[ranges.length - 1].end);
    }
    return "selected period";
}

function minPresent(a, b) {
    if (a === null) return b;
    if (b === null) return a;
    return Math.min(a, b);
}

function maxPresent(a, b) {
    if (a === null) return b;
    if (b === null) return a;
    return Math.max(a, b);
}

function formatScore(value) {
    if (value === null) return "—";
    return `${Math.round(value)}`;
}

function formatMilliseconds(value) {
    if (value === null) return "—";
    return `${Math.round(value)} ms`;
}

function formatPercent(value) {
    if (value === null) return "—";
    return `${Number(value).toFixed(1).replace(/\.0$/, "")}%`;
}

function badgeTone(statusKey) {
    switch (statusKey) {
        case "healthy":
            return "is-healthy";
        case "watch":
            return "is-watch";
        case "attention":
            return "is-attention";
        default:
            return "is-nodata";
    }
}

function healthColor(statusKey) {
    if (isColorBlindMode()) {
        switch (statusKey) {
            case "healthy":
                return "rgb(59, 82, 139)";
            case "watch":
                return "rgb(33, 145, 140)";
            case "attention":
                return "rgb(253, 231, 37)";
            default:
                return "#6b7280";
        }
    }
    switch (statusKey) {
        case "healthy":
            return "#2fbf71";
        case "watch":
            return "#f2c744";
        case "attention":
            return "#ef7f1a";
        default:
            return "#6b7280";
    }
}

function qooColor(value) {
    const numeric = toFiniteNumber(value);
    if (numeric === null) return NO_DATA_COLOR;
    return lerpGreenToRedViaOrange(clamp(numeric, 0, 100), 100);
}

function rttColor(value) {
    const numeric = toFiniteNumber(value);
    if (numeric === null) return NO_DATA_COLOR;
    return lerpGreenToRedViaOrange(200 - clamp(numeric, 0, 200), 200);
}

function retransmitColor(value) {
    const numeric = toFiniteNumber(value);
    if (numeric === null) return NO_DATA_COLOR;
    return lerpGreenToRedViaOrange(10 - clamp(numeric, 0, 10), 10);
}

function utilizationColor(value) {
    const numeric = toFiniteNumber(value);
    if (numeric === null) return NO_DATA_COLOR;
    return lerpGreenToRedViaOrange(100 - clamp(numeric, 0, 100), 100);
}

function sentimentBadge(status, compact = false) {
    return `
        <span class="site-rankings-status ${badgeTone(status.key)}${compact ? " is-compact" : ""}">
            <i class="fas ${status.icon}" aria-hidden="true"></i>
            <span>${escapeHtml(status.label)}</span>
        </span>
    `.trim();
}

function reasonChip(text) {
    return `<span class="site-rankings-reason">${escapeHtml(text)}</span>`;
}

function buildSiteHealthRow(row) {
    const ticks = Array.isArray(row?.ticks) ? row.ticks : [];
    const metrics = row?.metrics || {};
    const qoo = periodPairSummary(metrics.qoo_up || [], metrics.qoo_down || [], ticks);
    const rtt = periodSeriesSummary(metrics.rtt_down || [], ticks);
    const retransmit = periodPairSummary(metrics.retransmit_up || [], metrics.retransmit_down || [], ticks);
    const utilization = periodPairSummary(metrics.utilization_up || [], metrics.utilization_down || [], ticks);

    const overallQoo = minPresent(qoo.top, qoo.bottom);
    const qooStatus = sentimentState(qualityFromQoo(overallQoo));
    const rttStatus = sentimentState(qualityFromRtt(rtt.value));
    const retransmitOverall = maxPresent(retransmit.top, retransmit.bottom);
    const retransmitStatus = sentimentState(qualityFromRetransmit(retransmitOverall));
    const utilizationOverall = maxPresent(utilization.top, utilization.bottom);
    const utilizationStatus = sentimentState(qualityFromCapacity(utilizationOverall));
    const uploadUtilStatus = sentimentState(qualityFromCapacity(utilization.top));
    const downloadUtilStatus = sentimentState(qualityFromCapacity(utilization.bottom));

    const reasons = [];
    if (qooStatus.key === "attention") reasons.push("Low Quality Score");
    if (rttStatus.key === "attention" || rttStatus.key === "watch") reasons.push("High round-trip time");
    if (retransmitStatus.key === "attention" || retransmitStatus.key === "watch") reasons.push("High TCP retransmits");
    if (downloadUtilStatus.key === "attention" || downloadUtilStatus.key === "watch") reasons.push("High download utilization");
    if (uploadUtilStatus.key === "attention" || uploadUtilStatus.key === "watch") reasons.push("High upload utilization");

    let explanation = "";
    if (qooStatus.key === "nodata") {
        explanation = reasons.length > 0
            ? `Quality Score was unavailable for this site in the selected period. The strongest signs still point to ${reasons.slice(0, 2).join(" and ").toLowerCase()}.`
            : "Quality Score was unavailable for this site in the selected period, so it is flagged as no data.";
    } else if (reasons.length > 0) {
        explanation = `This site is ranking ${qooStatus.key === "attention" ? "poorly" : "below healthy"} mainly because of ${reasons.slice(0, 3).join(", ").replace(/, ([^,]*)$/, " and $1").toLowerCase()} over the selected period.`;
    } else if (qooStatus.key === "healthy") {
        explanation = "Upload and download Quality Score stayed healthy for the selected period.";
    } else {
        explanation = "Quality Score is in watch status for the selected period, but no single underlying metric clearly dominated.";
    }

    return {
        ...row,
        siteType: row.badge || "Site",
        qoo,
        rtt,
        retransmit,
        utilization,
        overallQoo,
        status: qooStatus,
        sortKey: [
            STATUS_ORDER[qooStatus.key] ?? 99,
            overallQoo === null ? 999 : overallQoo,
            qualityFromRtt(rtt.value) ?? 999,
            qualityFromRetransmit(retransmitOverall) ?? 999,
            qualityFromCapacity(utilizationOverall) ?? 999,
            (row.label || "").toLowerCase(),
        ],
        reasons,
        explanation,
        metricStates: {
            qoo: qooStatus,
            rtt: rttStatus,
            retransmit: retransmitStatus,
            utilization: utilizationStatus,
            uploadUtilization: uploadUtilStatus,
            downloadUtilization: downloadUtilStatus,
        },
    };
}

function compareRows(a, b) {
    const aKey = Array.isArray(a.sortKey) ? a.sortKey : [];
    const bKey = Array.isArray(b.sortKey) ? b.sortKey : [];
    const len = Math.max(aKey.length, bKey.length);
    for (let i = 0; i < len; i += 1) {
        const av = aKey[i];
        const bv = bKey[i];
        if (av === bv) continue;
        if (typeof av === "string" || typeof bv === "string") {
            return String(av).localeCompare(String(bv));
        }
        return Number(av) - Number(bv);
    }
    return 0;
}

function countByStatus(rows) {
    const counts = {
        attention: 0,
        watch: 0,
        healthy: 0,
        nodata: 0,
    };
    rows.forEach((row) => {
        counts[row.status.key] = (counts[row.status.key] || 0) + 1;
    });
    return counts;
}

function buildTrendData(rows) {
    const firstRow = rows.find((row) => Array.isArray(row.ticks) && row.ticks.length > 0);
    const ticks = Array.isArray(firstRow?.ticks) ? firstRow.ticks : [];
    const counts = {
        attention: new Array(ticks.length).fill(0),
        watch: new Array(ticks.length).fill(0),
        healthy: new Array(ticks.length).fill(0),
        nodata: new Array(ticks.length).fill(0),
    };

    rows.forEach((row) => {
        for (let i = 0; i < ticks.length; i += 1) {
            const qooUp = toFiniteNumber(row.metrics?.qoo_up?.[i]);
            const qooDown = toFiniteNumber(row.metrics?.qoo_down?.[i]);
            const overallQoo = minPresent(qooUp, qooDown);
            const status = sentimentState(qualityFromQoo(overallQoo));
            counts[status.key][i] += 1;
        }
    });

    return { ticks, counts };
}

function renderBucketCells(values, colorFn) {
    return (Array.isArray(values) ? values : []).map((value) => `
        <span class="site-rankings-strip-cell" style="background:${colorFn(value)}" aria-hidden="true"></span>
    `).join("");
}

function metricBlock({ label, value, status, description, stripHtml = "", extra = "" }) {
    return `
        <div class="site-rankings-metric-card">
            <div class="d-flex justify-content-between align-items-start gap-2">
                <div>
                    <div class="site-rankings-metric-label">${escapeHtml(label)}</div>
                    <div class="site-rankings-metric-value">${escapeHtml(value)}</div>
                </div>
                ${sentimentBadge(status, true)}
            </div>
            <div class="site-rankings-metric-text">${escapeHtml(description)}</div>
            ${extra}
            ${stripHtml}
        </div>
    `;
}

class SiteRankingsTrendGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = {
            grid: {
                left: 56,
                right: 12,
                top: 18,
                bottom: 28,
                containLabel: true,
            },
            legend: {
                top: 0,
                itemWidth: 10,
                itemHeight: 10,
                textStyle: {
                    color: "rgba(210, 220, 235, 0.82)",
                    fontSize: 11,
                },
                data: ["Needs attention", "Watch", "Healthy", "No data"],
            },
            xAxis: {
                type: "category",
                data: [],
                axisLabel: {
                    hideOverlap: true,
                    formatter: (value) => new Date(parseInt(value, 10) * 1000).toLocaleTimeString(),
                },
            },
            yAxis: {
                type: "value",
                axisLabel: { formatter: formatCountAxis },
                name: "Sites",
                nameLocation: "middle",
                nameGap: 38,
                splitNumber: 4,
            },
            tooltip: createBoundedAxisTooltip({
                yFormatter: formatCountAxis,
                formatter: (params) => {
                    if (!params || params.length === 0) return "";
                    const lines = params
                        .slice()
                        .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))
                        .map((param) => `${param.marker}<strong>${escapeHtml(param.seriesName)}</strong>: ${Number(param.value || 0).toLocaleString()}`);
                    return `
                        <div style="font-size:10px;">
                            ${new Date(parseInt(params[0].axisValue, 10) * 1000).toLocaleString()}<br/>
                            ${lines.join("<br/>")}
                        </div>
                    `;
                },
            }),
            series: [],
        };
        this.chart.setOption(this.option, true);
    }

    update(data) {
        this.chart.hideLoading();
        this.option.xAxis.data = data?.ticks || [];
        this.option.series = [
            {
                name: "Needs attention",
                type: "line",
                stack: "rankings",
                smooth: true,
                showSymbol: false,
                lineStyle: { width: 2, color: healthColor("attention") },
                areaStyle: { color: "rgba(239, 127, 26, 0.22)" },
                data: data?.counts?.attention || [],
            },
            {
                name: "Watch",
                type: "line",
                stack: "rankings",
                smooth: true,
                showSymbol: false,
                lineStyle: { width: 2, color: healthColor("watch") },
                areaStyle: { color: "rgba(242, 199, 68, 0.18)" },
                data: data?.counts?.watch || [],
            },
            {
                name: "Healthy",
                type: "line",
                stack: "rankings",
                smooth: true,
                showSymbol: false,
                lineStyle: { width: 2, color: healthColor("healthy") },
                areaStyle: { color: "rgba(47, 191, 113, 0.17)" },
                data: data?.counts?.healthy || [],
            },
            {
                name: "No data",
                type: "line",
                stack: "rankings",
                smooth: true,
                showSymbol: false,
                lineStyle: { width: 2, color: healthColor("nodata") },
                areaStyle: { color: "rgba(107, 114, 128, 0.18)" },
                data: data?.counts?.nodata || [],
            },
        ];
        this.chart.setOption(this.option, true);
    }
}

export class ShaperSiteRankings extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.state = createExecutiveState("shaper");
        this.executiveData = null;
        this.rankings = [];
        this.selectedHash = null;
        this.typeFilter = "all";
        this.healthFilter = "all";
        this.searchFilter = "";
        this.redactListener = () => this.render();
    }

    title() {
        return "Site Rankings";
    }

    tooltip() {
        return "<h5>Site Rankings</h5><p>Ranks sites by Quality Score (QoO) over the selected period and explains what is driving poor scores.</p>";
    }

    channelRequests() {
        return ["ShaperDetail", "SiteShaperPage"];
    }

    buildContainer() {
        const base = super.buildContainer();
        const shell = document.createElement("div");
        shell.className = "site-rankings-shell";
        shell.innerHTML = `
            <div class="site-rankings-intro" data-dashlet-tag="shaperInsightsRtt"></div>
            <div class="site-rankings-summary"></div>
            <div class="site-rankings-controls"></div>
            <div class="site-rankings-layout">
                <div class="site-rankings-list-pane" data-dashlet-tag="shaperInsightsRxmit">
                    <div class="site-rankings-pane-header">
                        <div>
                            <div class="site-rankings-pane-title">Worst sites over selected period</div>
                            <div class="site-rankings-pane-subtitle">Ranked by the worse of upload or download Quality Score.</div>
                        </div>
                    </div>
                    <div class="site-rankings-list"></div>
                </div>
                <div class="site-rankings-side">
                    <div class="site-rankings-trend-pane" data-dashlet-tag="shaperInsightsCapacityDown">
                        <div class="site-rankings-pane-header">
                            <div>
                                <div class="site-rankings-pane-title">Period trend</div>
                                <div class="site-rankings-pane-subtitle">How many filtered sites were healthy, watch, attention, or no data at each point.</div>
                            </div>
                        </div>
                        <div id="${this.id}_trend" class="site-rankings-trend"></div>
                    </div>
                    <div class="site-rankings-detail-pane" data-dashlet-tag="shaperInsightsCapacityUp">
                        <div class="site-rankings-pane-header">
                            <div>
                                <div class="site-rankings-pane-title">Selected site</div>
                                <div class="site-rankings-pane-subtitle">Use this drawer to see what is driving the score and open the site page.</div>
                            </div>
                        </div>
                        <div class="site-rankings-detail"></div>
                    </div>
                </div>
            </div>
        `;
        base.appendChild(shell);

        this.root = base;
        this.shell = shell;
        this.introEl = shell.querySelector(".site-rankings-intro");
        this.summaryEl = shell.querySelector(".site-rankings-summary");
        this.controlsEl = shell.querySelector(".site-rankings-controls");
        this.listEl = shell.querySelector(".site-rankings-list");
        this.detailEl = shell.querySelector(".site-rankings-detail");
        this.trendEl = shell.querySelector(`#${this.id}_trend`);

        return base;
    }

    setup() {
        super.setup();
        if (!this.trendGraph) {
            this.trendGraph = new SiteRankingsTrendGraph(`${this.id}_trend`);
            this.graphs.push(this.trendGraph);
        }
        this.bindEvents();
        window.addEventListener("lqos:redact-changed", this.redactListener);
        if (this.executiveData) {
            this.render();
        } else {
            this.renderEmpty();
        }
    }

    dispose() {
        window.removeEventListener("lqos:redact-changed", this.redactListener);
        super.dispose();
    }

    bindEvents() {
        if (this.boundEvents) return;
        this.boundEvents = true;

        this.controlsEl.addEventListener("input", (event) => {
            if (event.target?.matches("[data-site-rankings-search]")) {
                this.searchFilter = event.target.value || "";
                this.render();
            }
        });

        this.controlsEl.addEventListener("change", (event) => {
            if (event.target?.matches("[data-site-rankings-type]")) {
                this.typeFilter = event.target.value || "all";
                this.render();
            }
            if (event.target?.matches("[data-site-rankings-health]")) {
                this.healthFilter = event.target.value || "all";
                this.render();
            }
        });

        this.summaryEl.addEventListener("click", (event) => {
            const button = event.target.closest("[data-health-filter]");
            if (!button) return;
            const nextFilter = button.getAttribute("data-health-filter") || "all";
            this.healthFilter = this.healthFilter === nextFilter ? "all" : nextFilter;
            this.render();
        });

        this.listEl.addEventListener("click", (event) => {
            const rowButton = event.target.closest("[data-site-hash]");
            if (!rowButton) return;
            this.selectedHash = rowButton.getAttribute("data-site-hash");
            this.render();
        });
    }

    onMessage(msg) {
        if (!applyExecutiveMessage("shaper", this.state, msg)) {
            return;
        }
        const data = buildExecutiveData("shaper", this.state);
        if (!data) return;
        this.executiveData = data;
        const rows = (Array.isArray(data.rows) ? data.rows : [])
            .filter((row) => row.row_type === "Site")
            .map(buildSiteHealthRow)
            .sort(compareRows);
        this.rankings = rows;
        this.render();
    }

    renderEmpty(message = "Loading site rankings…") {
        this.introEl.innerHTML = `
            <div class="site-rankings-guide">
                <div class="site-rankings-guide-title">Quality Score action list</div>
                <div class="text-muted">${escapeHtml(message)}</div>
            </div>
        `;
        this.summaryEl.innerHTML = "";
        this.controlsEl.innerHTML = "";
        this.listEl.innerHTML = `<div class="site-rankings-empty">${escapeHtml(message)}</div>`;
        this.detailEl.innerHTML = `<div class="site-rankings-empty">Choose a site to see why it is ranking poorly.</div>`;
    }

    render() {
        if (!this.shell || !this.executiveData) {
            return;
        }

        disposeTooltips(this.root);

        const allRows = Array.isArray(this.rankings) ? this.rankings : [];
        if (allRows.length === 0) {
            const fallbackMessage = Number(this.executiveData?.header?.circuit_count || 0) > 0
                ? "This shaper is using a flat circuit topology, so there are no child sites to rank here. Use Circuits for the actionable list on this network."
                : "No sites were available for ranking in the selected period.";
            this.renderEmpty(fallbackMessage);
            if (this.trendGraph) {
                this.trendGraph.update({ ticks: [], counts: { attention: [], watch: [], healthy: [], nodata: [] } });
            }
            return;
        }

        const typeOptions = Array.from(new Set(allRows.map((row) => row.siteType).filter(Boolean))).sort((a, b) => a.localeCompare(b));
        const search = this.searchFilter.trim().toLowerCase();
        const baseRows = allRows.filter((row) => {
            if (this.typeFilter !== "all" && row.siteType !== this.typeFilter) return false;
            if (!search) return true;
            return (row.label || "").toLowerCase().includes(search);
        });
        const displayRows = baseRows.filter((row) => {
            if (this.healthFilter === "all") return true;
            return row.status.key === this.healthFilter;
        });
        const counts = countByStatus(baseRows);
        const trend = buildTrendData(baseRows);

        if (!displayRows.some((row) => row.hash === this.selectedHash)) {
            this.selectedHash = displayRows[0]?.hash || baseRows[0]?.hash || null;
        }
        const selectedRow = allRows.find((row) => row.hash === this.selectedHash) || null;

        this.introEl.innerHTML = `
            <div class="site-rankings-guide">
                <div class="site-rankings-guide-copy">
                    <div class="site-rankings-guide-title">Rank sites by Quality Score (QoO)</div>
                    <div class="site-rankings-guide-text">
                        This tab uses the same upload/download Quality Score model as Executive Summary. The list ranks the worst sites over the selected period, not just the latest sample. Open a site in the drawer to see whether latency, retransmits, or utilization is driving the score.
                    </div>
                </div>
                <div class="site-rankings-guide-meta">
                    <span class="site-rankings-guide-pill">Selected period</span>
                    <span class="site-rankings-guide-period">${escapeHtml(allRows[0]?.qoo?.rangeText || "selected period")}</span>
                </div>
            </div>
        `;

        this.summaryEl.innerHTML = `
            ${this.summaryButton("attention", "Needs attention", counts.attention, "Prioritize these first")}
            ${this.summaryButton("watch", "Watch", counts.watch, "Monitor these before they become persistent")}
            ${this.summaryButton("healthy", "Healthy", counts.healthy, "No immediate action needed")}
            ${this.summaryButton("nodata", "No data", counts.nodata, "No Quality Score available in this period")}
        `;

        this.controlsEl.innerHTML = `
            <div class="site-rankings-filter-group">
                <label class="site-rankings-filter-label" for="${this.id}_type">Type</label>
                <select class="form-select form-select-sm" id="${this.id}_type" data-site-rankings-type>
                    <option value="all"${this.typeFilter === "all" ? " selected" : ""}>All site types</option>
                    ${typeOptions.map((type) => `<option value="${escapeAttr(type)}"${this.typeFilter === type ? " selected" : ""}>${escapeHtml(type)}</option>`).join("")}
                </select>
            </div>
            <div class="site-rankings-filter-group">
                <label class="site-rankings-filter-label" for="${this.id}_health">Health</label>
                <select class="form-select form-select-sm" id="${this.id}_health" data-site-rankings-health>
                    <option value="all"${this.healthFilter === "all" ? " selected" : ""}>All health states</option>
                    <option value="attention"${this.healthFilter === "attention" ? " selected" : ""}>Needs attention</option>
                    <option value="watch"${this.healthFilter === "watch" ? " selected" : ""}>Watch</option>
                    <option value="healthy"${this.healthFilter === "healthy" ? " selected" : ""}>Healthy</option>
                    <option value="nodata"${this.healthFilter === "nodata" ? " selected" : ""}>No data</option>
                </select>
            </div>
            <div class="site-rankings-filter-group is-search">
                <label class="site-rankings-filter-label" for="${this.id}_search">Search</label>
                <input type="search" id="${this.id}_search" class="form-control form-control-sm" data-site-rankings-search placeholder="Find a site" value="${escapeAttr(this.searchFilter)}">
            </div>
        `;

        if (displayRows.length === 0) {
            this.listEl.innerHTML = `<div class="site-rankings-empty">No sites matched the current filters.</div>`;
        } else {
            this.listEl.innerHTML = displayRows.map((row, index) => this.renderRow(row, index)).join("");
        }

        this.detailEl.innerHTML = selectedRow
            ? this.renderDetail(selectedRow)
            : `<div class="site-rankings-empty">Choose a site to see why it is ranking poorly.</div>`;

        if (this.trendGraph) {
            this.trendGraph.update(trend);
        }

        enableTooltips(this.root);
    }

    summaryButton(key, label, count, helper) {
        const active = this.healthFilter === key;
        return `
            <button type="button" class="site-rankings-summary-card ${badgeTone(key)}${active ? " is-active" : ""}" data-health-filter="${key}" aria-pressed="${active ? "true" : "false"}">
                <div class="site-rankings-summary-count">${Number(count || 0).toLocaleString()}</div>
                <div class="site-rankings-summary-label">${escapeHtml(label)}</div>
                <div class="site-rankings-summary-help">${escapeHtml(helper)}</div>
            </button>
        `;
    }

    renderRow(row, index) {
        const active = row.hash === this.selectedHash;
        const reasons = row.reasons.length > 0 ? row.reasons.slice(0, 3).map(reasonChip).join("") : reasonChip("No single driver stood out");
        return `
            <button type="button" class="site-rankings-row${active ? " is-active" : ""}" data-site-hash="${escapeAttr(row.hash)}">
                <div class="site-rankings-row-rank">${index + 1}</div>
                <div class="site-rankings-row-main">
                    <div class="site-rankings-row-head">
                        <div class="site-rankings-row-title">${escapeHtml(redactText(row.label || "Unnamed site"))}</div>
                        <span class="site-rankings-type-badge">${escapeHtml(row.siteType || "Site")}</span>
                    </div>
                    <div class="site-rankings-row-reasons">${reasons}</div>
                </div>
                <div class="site-rankings-row-qoo">
                    <div class="site-rankings-mini-stat">
                        <span class="site-rankings-mini-label">Upload</span>
                        <strong>${escapeHtml(formatScore(row.qoo.top))}</strong>
                    </div>
                    <div class="site-rankings-mini-stat">
                        <span class="site-rankings-mini-label">Download</span>
                        <strong>${escapeHtml(formatScore(row.qoo.bottom))}</strong>
                    </div>
                </div>
                <div class="site-rankings-row-status">
                    ${sentimentBadge(row.status)}
                </div>
            </button>
        `;
    }

    renderDetail(row) {
        const title = redactText(row.label || "Unnamed site");
        const qooStrip = `
            <div class="site-rankings-strip-block">
                <div class="site-rankings-strip-labels"><span>Upload</span><span>Download</span></div>
                <div class="site-rankings-strip-rows">
                    <div class="site-rankings-strip-row">${renderBucketCells(row.qoo.topBuckets, qooColor)}</div>
                    <div class="site-rankings-strip-row">${renderBucketCells(row.qoo.bottomBuckets, qooColor)}</div>
                </div>
            </div>
        `;
        const retransStrip = `
            <div class="site-rankings-strip-block">
                <div class="site-rankings-strip-labels"><span>Upload</span><span>Download</span></div>
                <div class="site-rankings-strip-rows">
                    <div class="site-rankings-strip-row">${renderBucketCells(row.retransmit.topBuckets, retransmitColor)}</div>
                    <div class="site-rankings-strip-row">${renderBucketCells(row.retransmit.bottomBuckets, retransmitColor)}</div>
                </div>
            </div>
        `;
        const utilStrip = `
            <div class="site-rankings-strip-block">
                <div class="site-rankings-strip-labels"><span>Upload</span><span>Download</span></div>
                <div class="site-rankings-strip-rows">
                    <div class="site-rankings-strip-row">${renderBucketCells(row.utilization.topBuckets, utilizationColor)}</div>
                    <div class="site-rankings-strip-row">${renderBucketCells(row.utilization.bottomBuckets, utilizationColor)}</div>
                </div>
            </div>
        `;
        const rttStrip = `
            <div class="site-rankings-strip-block">
                <div class="site-rankings-strip-row">${renderBucketCells(row.rtt.buckets, rttColor)}</div>
            </div>
        `;

        return `
            <div class="site-rankings-detail-header">
                <div>
                    <div class="site-rankings-detail-title">${escapeHtml(title)}</div>
                    <div class="site-rankings-detail-subtitle">
                        <span class="site-rankings-type-badge">${escapeHtml(row.siteType || "Site")}</span>
                        <span>${escapeHtml(row.qoo.rangeText)}</span>
                    </div>
                </div>
                <div class="site-rankings-detail-actions">
                    ${sentimentBadge(row.status)}
                    <a class="btn btn-sm btn-outline-primary" href="${escapeAttr(row.link)}">Open site</a>
                </div>
            </div>
            <div class="site-rankings-detail-copy">${escapeHtml(row.explanation)}</div>
            <div class="site-rankings-detail-kpis">
                <div class="site-rankings-kpi">
                    <div class="site-rankings-kpi-label">Quality Score</div>
                    <div class="site-rankings-kpi-value">${escapeHtml(formatScore(row.qoo.top))} / ${escapeHtml(formatScore(row.qoo.bottom))}</div>
                    <div class="site-rankings-kpi-help">Upload / Download typical values</div>
                </div>
                <div class="site-rankings-kpi">
                    <div class="site-rankings-kpi-label">Median RTT</div>
                    <div class="site-rankings-kpi-value">${escapeHtml(formatMilliseconds(row.rtt.value))}</div>
                    <div class="site-rankings-kpi-help">Typical round-trip latency</div>
                </div>
                <div class="site-rankings-kpi">
                    <div class="site-rankings-kpi-label">TCP Retransmits</div>
                    <div class="site-rankings-kpi-value">${escapeHtml(formatPercent(row.retransmit.top))} / ${escapeHtml(formatPercent(row.retransmit.bottom))}</div>
                    <div class="site-rankings-kpi-help">Upload / Download typical values</div>
                </div>
                <div class="site-rankings-kpi">
                    <div class="site-rankings-kpi-label">Utilization</div>
                    <div class="site-rankings-kpi-value">${escapeHtml(formatPercent(row.utilization.top))} / ${escapeHtml(formatPercent(row.utilization.bottom))}</div>
                    <div class="site-rankings-kpi-help">Upload / Download headroom check</div>
                </div>
            </div>
            <div class="site-rankings-metric-grid">
                ${metricBlock({
                    label: "Quality Score (QoO)",
                    value: `${formatScore(row.qoo.top)} upload / ${formatScore(row.qoo.bottom)} download`,
                    status: row.metricStates.qoo,
                    description: "Higher is better. This is the score used to rank the site.",
                    stripHtml: qooStrip,
                })}
                ${metricBlock({
                    label: "Round-trip time",
                    value: formatMilliseconds(row.rtt.value),
                    status: row.metricStates.rtt,
                    description: "Lower latency is better. This helps explain responsiveness problems.",
                    stripHtml: rttStrip,
                })}
                ${metricBlock({
                    label: "TCP retransmits",
                    value: `${formatPercent(row.retransmit.top)} upload / ${formatPercent(row.retransmit.bottom)} download`,
                    status: row.metricStates.retransmit,
                    description: "Higher retransmits usually point to loss or unstable links.",
                    stripHtml: retransStrip,
                })}
                ${metricBlock({
                    label: "Capacity utilization",
                    value: `${formatPercent(row.utilization.top)} upload / ${formatPercent(row.utilization.bottom)} download`,
                    status: row.metricStates.utilization,
                    description: "Higher utilization means less headroom and more chance of congestion.",
                    stripHtml: utilStrip,
                })}
            </div>
        `;
    }
}
